<template>
  <v-container class="pt-5">
    <h1>Quality control</h1>
    <v-btn small outlined class="mb-4" @click="qcDialog = true">Asiat</v-btn>
    <QualityControlDialog
      v-model="qcDialog"
      :items="qcItems"
      @update-items="onUpdateQCItems"
    />
    <v-row>
      <v-col cols="12" md="3">
        <v-select
          v-if="leagueOptions.length"
          :items="leagueOptions"
          v-model="selectedLeague"
          item-text="league_name"
          item-value="id"
          label="Liiga"
          class="mb-4"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          :items="yearOptions"
          v-model="selectedYear"
          item-text="text"
          item-value="value"
          label="Vuosi"
          class="mb-4"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-if="analyzerOptions.length"
          :items="analyzerOptions"
          v-model="selectedAnalyzer"
          item-text="name"
          item-value="id"
          label="Analysoija"
          class="mb-4"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          :items="sortOptions"
          v-model="selectedSort"
          item-text="text"
          item-value="value"
          label="Järjestys"
          class="mb-4"
        />
      </v-col>
    </v-row>
    <v-simple-table v-if="games.length">
      <thead>
        <tr>
          <th>Liiga</th>
          <th>Aika</th>
          <th>Analysoija</th>
          <th>Peli</th>
          <th>Virheet</th>
          <th>Katsottu</th>
        </tr>
      </thead>
      <tbody>
        <QualityControlGameRow
          :analyzers="analyzers"
          v-for="game in games"
          :key="game.id"
          :game="game"
          :qc-items="qcItems"
          :dialog-open="openGameDialogId === game.id"
          :selected-row-id="selectedRowId"
          @open-dialog="openGameDialog"
          @close-dialog="closeGameDialog"
        />
      </tbody>
    </v-simple-table>
    <div v-else>
      No games found.
    </div>
  </v-container>
</template>

<script>
import ax from '@/utils/axios/axios.js'
import QualityControlDialog from '@/components/quality/QualityControlDialog.vue'
import QualityControlGameRow from '@/components/quality/QualityControlGameRow.vue'

function getYearRange(start, end) {
  const years = []
  for (let y = end; y >= start; y--) years.push({ value: y, text: y.toString() })
  return years
}

export default {
  name: 'QualityControl',
  components: { QualityControlDialog, QualityControlGameRow },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      games: [],
      analyzers: [],
      leagues: [],
      selectedLeague: null,
      selectedYear: 2025,
      selectedAnalyzer: null,
      selectedSort: 'desc',
      loading: false,
      yearOptions: [{ value: null, text: 'Kaikki vuodet' }, ...getYearRange(currentYear - 1, currentYear + 1)],
      sortOptions: [
        { value: 'desc', text: 'Uusin ensin' },
        { value: 'asc', text: 'Vanhin ensin' }
      ],
      qcDialog: false,
      qcItems: [],
      filterDialog: { show: false, idx: null, filter: null },
      archiveDialog: { show: false, idx: null, item: null },
      isRestoringScroll: false,
      initializing: true,
      selectedRowId: null
    }
  },
  computed: {
    leagueOptions() {
      return [{ id: null, league_name: 'Kaikki liigat' }, ...this.leagues.filter(l => !l.archived)]
    },
    analyzerOptions() {
      return [{ id: null, name: 'Kaikki analysoijat' }, ...this.analyzers]
    },
    openGameDialogId() {
      return this.$route.query.gameDialog || null;
    }
  },
  mounted() {
    // Add scroll listener to save position as user scrolls
    this.scrollHandler = this.debounce(() => {
      this.saveState()
    }, 100)

    window.addEventListener('scroll', this.scrollHandler)
  },
  beforeDestroy() {
    // Remove scroll listener
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
    }

    // Save current scroll position
    this.saveState()
  },
  async created() {
    this.initializing = true;
    this.loadState();
    await this.fetchLeagues();
    await this.fetchQCItems();
    await this.fetchAnalyzers();
    await this.fetchGames();
    this.initializing = false;
  },
  methods: {
    async fetchLeagues() {
      try {
        const res = await ax.get('/league')
        this.leagues = res.data
      } catch (e) {
        this.leagues = []
      }
    },
    async fetchAnalyzers() {
      try {
        const res = await ax.get('/analyzer')
        this.analyzers = res.data
      } catch (e) {
        this.analyzers = []
      }
    },
    async fetchGames() {
      this.loading = true
      try {
        const params = { sort: this.selectedSort };
        if (this.selectedLeague) params.league_id = this.selectedLeague;
        if (this.selectedYear) params.year = this.selectedYear;
        if (this.selectedAnalyzer) params.analyzer = this.selectedAnalyzer;

        console.log('Fetching games with params:', params);

        // Fetch all games for the filters (no limit/offset)
        const res = await ax.get('/league/games', { params });
        console.log('Games response:', res.data);
        this.games = res.data;
        // Restore scroll position after games are loaded
        this.$nextTick(() => {
          this.restoreScrollPosition();
        });
      } catch (e) {
        console.error('Error fetching games:', e);
        this.games = [];
      }
      this.loading = false;
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}.${month}.${year}`
    },
    async fetchQCItems() {
      try {
        const res = await ax.get('/quality')
        this.qcItems = res.data.map(i => ({ ...i, editing: false }))
      } catch (e) {
        this.qcItems = []
      }
    },
    onUpdateQCItems(items) {
      this.qcItems = items
    },
    addQCItem() {
      this.qcItems.push({ id: null, title: '', description: '', filter: {}, checked_date: '', archived: false, editing: true })
    },
    editQCItem(idx) {
      this.qcItems[idx].editing = true
    },
    async saveQCItem(idx) {
      const item = this.qcItems[idx]
      try {
        let res
        if (item.id) {
          res = await ax.put(`/quality/${item.id}`, { ...item, filter: item.filter })
        } else {
          res = await ax.post('/quality', { ...item, filter: item.filter })
        }
        this.$set(this.qcItems, idx, { ...res.data, editing: false })
      } catch (e) {
        // Optionally handle error
      }
    },
    editFilter(idx) {
      this.filterDialog = { show: true, idx, filter: { ...this.qcItems[idx].filter } }
    },
    saveFilter() {
      if (this.filterDialog.idx !== null) {
        this.qcItems[this.filterDialog.idx].filter = { ...this.filterDialog.filter }
      }
      this.filterDialog = { show: false, idx: null, filter: null }
    },
    confirmArchive(idx) {
      this.archiveDialog = { show: true, idx, item: this.qcItems[idx] }
    },
    async archiveConfirmed() {
      const { idx, item } = this.archiveDialog
      if (item.id) {
        try {
          await ax.put(`/quality/${item.id}`, { ...item, archived: true })
          this.qcItems[idx].archived = true
        } catch (e) { /* handle error if needed */ }
      } else {
        this.qcItems.splice(idx, 1)
      }
      this.archiveDialog = { show: false, idx: null, item: null }
    },
    saveState() {
      if (this.isRestoringScroll) return;
      const state = {
        selectedLeague: this.selectedLeague,
        selectedYear: this.selectedYear,
        selectedAnalyzer: this.selectedAnalyzer,
        selectedSort: this.selectedSort,
        scrollY: window.scrollY
      };
      localStorage.setItem('qualityControlState', JSON.stringify(state));
      // Only update URL if changed
      const newQuery = {};
      if (this.selectedLeague) newQuery.league = this.selectedLeague;
      if (this.selectedYear) newQuery.year = this.selectedYear;
      if (this.selectedAnalyzer) newQuery.analyzer = this.selectedAnalyzer;
      if (this.selectedSort) newQuery.sort = this.selectedSort;
      const currentQuery = this.$route.query;
      const hasChanged = JSON.stringify(newQuery) !== JSON.stringify(currentQuery);
      if (hasChanged) {
        this.$router.replace({ query: newQuery }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err);
          }
        });
      }
    },
    openGameDialog(gameId) {
      console.log('Opening game dialog for:', gameId);
      // Add game dialog to URL query
      const newQuery = { ...this.$route.query, gameDialog: gameId };
      this.$router.replace({ query: newQuery }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
      // Store selected row in localStorage and update local state
      localStorage.setItem('qualityControlSelectedRow', gameId);
      this.selectedRowId = gameId;
    },
    closeGameDialog() {
      // Remove game dialog from URL query
      const newQuery = { ...this.$route.query };
      delete newQuery.gameDialog;
      this.$router.replace({ query: newQuery }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },
        loadState() {
      const savedState = localStorage.getItem('qualityControlState');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          this.selectedLeague = state.selectedLeague;
          this.selectedYear = state.selectedYear;
          this.selectedAnalyzer = state.selectedAnalyzer;
          this.selectedSort = state.selectedSort;
        } catch (e) {
          console.warn('Failed to parse saved state:', e);
        }
      }
      const query = this.$route.query;
      if (query.league) this.selectedLeague = parseInt(query.league);
      if (query.year) this.selectedYear = parseInt(query.year);
      if (query.analyzer) this.selectedAnalyzer = query.analyzer;
      if (query.sort) this.selectedSort = query.sort;

      // Restore selected row ID from localStorage
      const savedSelectedRow = localStorage.getItem('qualityControlSelectedRow');
      if (savedSelectedRow) {
        this.selectedRowId = savedSelectedRow;
      }
    },
    restoreScrollPosition() {
      const savedState = localStorage.getItem('qualityControlState')
      if (savedState) {
        try {
          const state = JSON.parse(savedState)
          if (state.scrollY && state.scrollY > 0) {
            this.isRestoringScroll = true

            // Use requestAnimationFrame to ensure DOM is fully rendered
            requestAnimationFrame(() => {
              window.scrollTo(0, state.scrollY)

              // Re-enable scroll saving after a short delay
              setTimeout(() => {
                this.isRestoringScroll = false
              }, 500)
            })
          }
        } catch (e) {
          console.warn('Failed to restore scroll position:', e)
          this.isRestoringScroll = false
        }
      }
    },
    debounce(func, wait) {
      let timeout
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    }
  },
  watch: {
    selectedLeague(newVal, oldVal) {
      if (!this.initializing && newVal !== oldVal) {
        this.fetchGames();
      }
    },
    selectedYear(newVal, oldVal) {
      if (!this.initializing && newVal !== oldVal) {
        this.fetchGames();
      }
    },
    selectedAnalyzer(newVal, oldVal) {
      if (!this.initializing && newVal !== oldVal) {
        this.fetchGames();
      }
    },
    selectedSort(newVal, oldVal) {
      if (!this.initializing && newVal !== oldVal) {
        this.fetchGames();
      }
    }
  }
}
</script>